import type { AgentInfo } from "@/agents/info"
import { ANNOTATION_VALUE } from "@/constants/annotation-values"
import { deriveYesNoString } from "@/metrics/metric-display"
import type { MetricRecord } from "@/metrics/runtime"

import { logger } from "@/lib/logger"
import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type { SubvalueByTimestamp } from "@/types/api/MetricOutput"
import type { SimulatorType } from "@/types/api/SimulatorType"
import type { MetricRecordData } from "@/types/metrics/contracts"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
} from "@/types/RunMetricOutput"

import { SingleFloat } from "../../components/single/SingleFloat"
import { parseResultJson } from "../helpers/parseResultJson"
import { BinaryLLMMetricRecord } from "./BinaryLLMMetricRecord"

interface AgentFailsToRespondExplanationEvent {
  type: string
  start: number
  end?: number
  gap?: number
  text?: string
  explanation: string
}

interface AgentFailsToRespondResultJson {
  metadata?: { count?: number }
  unresponsiveness_events?: AgentFailsToRespondExplanationEvent[]
}

const log = logger("AgentFailsToRespondMetric")

const subvalueToEvent = (
  sv: SubvalueByTimestamp,
): AgentFailsToRespondExplanationEvent => {
  const gap = sv.float_value ?? sv.end_offset - sv.start_offset
  const type = sv.string_value ?? "agent never responded"
  return {
    type,
    start: sv.start_offset,
    end: sv.end_offset,
    gap,
    explanation: `Agent did not respond for ${gap.toFixed(1)}s at ${sv.start_offset.toFixed(1)}s`,
  }
}

const getEvents = (
  mo: Pick<
    RunMetricOutput,
    "subvalues_by_timestamp" | "result" | "result_json"
  >,
): AgentFailsToRespondExplanationEvent[] => {
  const subvalues = mo.subvalues_by_timestamp ?? []
  if (subvalues.length > 0) {
    return subvalues.map(subvalueToEvent)
  }
  const resultJson = parseResultJson<AgentFailsToRespondResultJson>(mo)
  return resultJson?.unresponsiveness_events ?? []
}

const getEventCount = (
  mo: Pick<
    RunMetricOutput,
    "subvalues_by_timestamp" | "result" | "result_json"
  >,
): number => {
  const subvalues = mo.subvalues_by_timestamp ?? []
  if (subvalues.length > 0) {
    return subvalues.length
  }
  const resultJson = parseResultJson<AgentFailsToRespondResultJson>(mo)
  return (
    resultJson?.metadata?.count ??
    resultJson?.unresponsiveness_events?.length ??
    0
  )
}

export class AgentFailsToRespondMetricRecord extends BinaryLLMMetricRecord {
  invertYesNoColors = true
  aggregate_units = "%"

  renderReadOnlyMetadata(): React.ReactNode {
    return null
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
    textAreaRef?: React.RefObject<HTMLTextAreaElement>,
    editorContext?: Parameters<MetricRecord["renderEditorMetadata"]>[3],
  ): React.ReactNode {
    if (editorContext?.readOnly) {
      return null
    }

    return super.renderEditorMetadata(metricRecordData, onChange, textAreaRef)
  }

  isMetricEnabled(
    _agent: AgentInfo,
    _simulatorType: SimulatorType,
  ): { result: boolean; error_message: string } {
    return { result: true, error_message: "" }
  }

  _renderSimulationCard(mo: RunMetricOutput): React.ReactNode {
    if (deriveYesNoString(mo) === ANNOTATION_VALUE.UNKNOWN) {
      return null
    }
    // Delegate to the base class so invertYesNoColors is respected
    // ("NO" — the agent responded — is the positive outcome).
    return super._renderSimulationCard(mo)
  }

  getSingleMetricOutputNode(mo: RunMetricOutput): React.ReactNode {
    const totalCount = getEventCount(mo)
    return (
      <SingleFloat
        mo={{ ...mo, float_value: totalCount }}
        units="events"
        formatFn={(v) => String(v)}
      />
    )
  }

  getHeaderValue(mo: RunMetricOutput[]): string | null {
    // % of calls where the agent failed to respond (YES); 0% is healthy.
    return this.getFailureRateHeaderValue(mo)
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    try {
      const formatTime = (s: number) =>
        `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`

      const events = getEvents(mo)
      const totalCount = events.length
      // Calculate statistics
      const totalUnresponsiveTime = events.reduce(
        (sum: number, e: AgentFailsToRespondExplanationEvent) => {
          const duration = e.gap ?? (e.end && e.start ? e.end - e.start : 0)
          return sum + duration
        },
        0,
      )

      const avgGap = totalCount > 0 ? totalUnresponsiveTime / totalCount : 0
      const longestGap = events.reduce(
        (max: number, e: AgentFailsToRespondExplanationEvent) => {
          const duration = e.gap ?? (e.end && e.start ? e.end - e.start : 0)
          return Math.max(max, duration)
        },
        0,
      )

      const getEventTypeInfo = (type: string) => {
        switch (type) {
          case "long_gap":
          case "long_gap_with_prompt":
            return {
              label: "Long Gap",
              color: "bg-background text-foreground border-divider",
            }
          case "repeated_prompt":
            return {
              label: "Repeated Prompt",
              color: "bg-background text-foreground border-divider",
            }
          case "agent unresponsive":
          case "agent_unresponsive":
            return {
              label: "Agent Unresponsive",
              color: "bg-background text-foreground border-divider",
            }
          case "agent never responded":
          case "agent_never_responded":
            return {
              label: "Agent Never Responded",
              color: "bg-background text-foreground border-divider",
            }
          default:
            return {
              label: type?.replace(/_/g, " ") ?? "Event",
              color: "bg-background text-foreground border-divider",
            }
        }
      }

      return (
        <div className="space-y-6">
          {/* Summary Statistics */}
          <div className="grid grid-cols-2 gap-4">
            <div className="border border-divider bg-content1 rounded-lg p-4">
              <div className="text-sm text-coval-text-muted font-medium mb-1">
                Total Events
              </div>
              <div className="text-sm font-bold text-foreground">
                {totalCount}
              </div>
            </div>

            <div className="border border-divider bg-content1 rounded-lg p-4">
              <div className="text-sm text-coval-text-muted font-medium mb-1">
                Total Unresponsive Time
              </div>
              <div className="text-sm font-bold text-foreground">
                {totalUnresponsiveTime.toFixed(1)}s
              </div>
            </div>

            <div className="border border-divider bg-content1 rounded-lg p-4">
              <div className="text-sm text-coval-text-muted font-medium mb-1">
                Average Gap
              </div>
              <div className="text-sm font-bold text-foreground">
                {avgGap.toFixed(1)}s
              </div>
            </div>

            <div className="border border-divider bg-content1 rounded-lg p-4">
              <div className="text-sm text-coval-text-muted font-medium mb-1">
                Longest Gap
              </div>
              <div className="text-sm font-bold text-foreground">
                {longestGap.toFixed(1)}s
              </div>
            </div>
          </div>

          {/* Events List */}
          <div>
            <label className="text-sm font-semibold mb-4 text-foreground block">
              Unresponsiveness Events
            </label>
            <div className="space-y-3">
              {events.map(
                (e: AgentFailsToRespondExplanationEvent, i: number) => {
                  const typeInfo = getEventTypeInfo(e.type)
                  const range =
                    e.end && e.end !== e.start
                      ? `${formatTime(e.start)} – ${formatTime(e.end)}`
                      : formatTime(e.start)
                  const duration =
                    e.gap ?? (e.end && e.start ? e.end - e.start : 0)

                  return (
                    <div
                      key={i}
                      className="border border-divider rounded-lg p-4 bg-background"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className={`px-2 py-1 rounded text-sm font-semibold border ${typeInfo.color}`}
                        >
                          {typeInfo.label}
                        </span>
                        <span className="text-sm text-coval-text-muted font-mono">
                          {range}
                        </span>
                        {duration > 0 && (
                          <span className="text-sm font-bold text-red-600">
                            {duration.toFixed(1)}s gap
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-coval-text-muted leading-relaxed">
                        {e.explanation}
                      </p>
                      {e.text && (
                        <div className="mt-2 p-2 bg-content1 rounded text-sm text-coval-text-muted italic">
                          "{e.text}"
                        </div>
                      )}
                    </div>
                  )
                },
              )}
            </div>
          </div>

          {totalCount === 0 && (
            <div className="text-center py-8 text-sm text-coval-text-muted">
              No unresponsiveness events detected
            </div>
          )}
        </div>
      )
    } catch (error) {
      log.error(
        "Error parsing explanation for unresponsiveness metric",
        error instanceof Error ? error : undefined,
      )
      return <div className="text-red-500">Error displaying metric details</div>
    }
  }

  renderMetricType(): React.ReactNode {
    return "Audio Metric"
  }

  getAudioRegions(
    mo: MetricOutputDisplaySource | undefined,
  ): AudioRegionParams[] {
    if (!mo) {
      return []
    }

    try {
      // Use subvalues_by_timestamp for waveform regions (preferred source)
      const subvalues = mo.subvalues_by_timestamp
      if (subvalues && subvalues.length > 0) {
        return subvalues.map((sv, i) => ({
          id: `unresponsive:${i}`,
          start: sv.start_offset,
          end: sv.end_offset,
          color: "rgba(255, 0, 0, 0.3)", // red
          drag: false,
          resize: false,
        }))
      }

      // @deprecated fallback: result_json.unresponsiveness_events — remove after subvalues_by_timestamp migration is complete
      const resultJson = parseResultJson<AgentFailsToRespondResultJson>(mo)
      const events: AgentFailsToRespondExplanationEvent[] =
        resultJson?.unresponsiveness_events ?? []
      return events
        .filter((e) => typeof e.start === "number" && typeof e.end === "number")
        .map((e, i) => ({
          id: `unresponsive:${i}`,
          start: e.start,
          end: Math.max(e.end!, e.start + 0.5),
          color: "rgba(255, 0, 0, 0.3)", // red
          drag: false,
          resize: false,
        }))
    } catch (err) {
      log.error(
        "Failed to parse agent unresponsiveness regions",
        err instanceof Error ? err : undefined,
      )
      return []
    }
  }
}
